/**
 * Единый механизм возврата «Назад» из карточек сотрудника/рабочего места (D1,
 * p3-structure-sout-training §2). Каждая точка перехода на карточку передаёт
 * `state: backState(location)` — снимок текущего адреса (pathname+search+hash) страницы,
 * с которой выполнен переход.
 *
 * Кнопка «Назад» читает `location.state.from` только как ПРИЗНАК «пришли внутренним переходом»,
 * а не как строку для push (code-review волны 3, HIGH): при наличии `state.from` возврат делает
 * `navigate(-1)` — снятие текущей history-записи восстанавливает ЕЁ СОБСТВЕННЫЙ `state` у записи,
 * куда мы возвращаемся, поэтому цепочки любой глубины (`/sout` → место → сотрудник → «Назад» →
 * место, у которого `state.from=/sout` жив → «Назад» → `/sout`) работают сами, без ручной
 * реконструкции пути. Push строки `state.from` вместо `navigate(-1)` (прежняя реализация) ломал
 * такие цепочки: у промежуточной страницы после push’а состояние `state` не восстанавливается —
 * второе нажатие «Назад» падало на фолбэк вместо следующего звена цепочки.
 *
 * Без `state.from` (прямой заход по URL, перезагрузка страницы, открытие в новой вкладке — history
 * state отсутствует) — `navigate(fallback)` (push), текущее поведение до D1.
 */
import { useLocation, useNavigate, type Location } from 'react-router-dom'

/** Форма `location.state`, как её ЧИТАЕТ `useBackTarget` — `from` может отсутствовать (deep-link). */
export interface BackState {
  from?: string
}

/** Снимок текущего адреса для передачи в `state.from` при переходе на карточку сотрудника/места
 * (`from` всегда присутствует — в отличие от `BackState`, которую читает потребитель). */
export function backState(location: Pick<Location, 'pathname' | 'search' | 'hash'>): { from: string } {
  return { from: `${location.pathname}${location.search}${location.hash}` }
}

/**
 * Функция перехода «Назад»: `navigate(-1)`, если переход внутренний (`state.from` задан), иначе
 * `navigate(fallback)` (deep-link/перезагрузка/новая вкладка).
 */
export function useBackTarget(fallback: string): () => void {
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state as BackState | null
  return () => {
    if (state?.from) {
      navigate(-1)
    } else {
      navigate(fallback)
    }
  }
}
